import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  CloudDownload,
  Description,
  CreditCard,
  Warning,
  Refresh,
  PictureAsPdf,
  AttachMoney,
  EuroSymbol,
  MoreVert,
  Block,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { usePaginationWithUrl } from '../../../hooks/usePaginationWithUrl';
import { PAYMENT_STATUS } from '../../../constants/paymentStatus';
import {
  getPendingPeppolInvoices,
  getDeliveredPeppolInvoices,
  markPeppolInvoiceAsDelivered,
  checkNewPeppolInvoices,
  syncDeliveredInvoices,
  downloadPeppolAttachment,
  updatePeppolInvoicePaymentStatus,
} from '../services/invoiceService';

const ReceivedPeppolInvoices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Pagination with URL params for delivered tab
  const { 
    page: deliveredPage, 
    pageSize: deliveredRowsPerPage, 
    handlePageChange: handleDeliveredPageChange, 
    handlePageSizeChange: handleDeliveredPageSizeChange 
  } = usePaginationWithUrl({ defaultPageSize: 10 });
  
  const [activeTab, setActiveTab] = useState(0);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [deliveredInvoices, setDeliveredInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, invoice: null });
  const [successDialog, setSuccessDialog] = useState({ open: false, message: '' });
  const [paymentDialog, setPaymentDialog] = useState({ open: false, invoice: null, isMarkingAsPaid: true });
  const [paymentForm, setPaymentForm] = useState({ paidDate: new Date().toISOString().split('T')[0], paymentSource: '', paymentNotes: '' });
  const [paymentSubmitAttempted, setPaymentSubmitAttempted] = useState(false);
  const [pendingPage, setPendingPage] = useState(0);
  const [totalDeliveredCount, setTotalDeliveredCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const ROWS_PER_PAGE = 10;

  const handleMenuOpen = (event, invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  // Check for new incoming invoices
  const handleCheckNewInvoices = async () => {
    setChecking(true);
    try {
      const response = await checkNewPeppolInvoices();
      if (response.success) {
        const newCount = response.count || 0;
        toast.success(`${response.message || `Found ${newCount} pending inbound documents`}`);
        // Refresh pending invoices list
        if (activeTab === 1) {
          await fetchPendingInvoices();
        } else {
          // Switch to pending tab to show new invoices
          setActiveTab(1);
        }
      } else {
        toast.warning(response.message || 'No new invoices found');
      }
    } catch (err) {
      console.error('Error checking new invoices:', err);
      toast.error(err.response?.data?.message || 'Error checking for new invoices');
    } finally {
      setChecking(false);
    }
  };

  // Sync delivered invoices
  const handleSyncDeliveredInvoices = async () => {
    setSyncing(true);
    try {
      const response = await syncDeliveredInvoices();
      if (response.success) {
        const syncedCount = response.count || 0;
        toast.success(`${response.message || `Synced ${syncedCount} delivered invoices`}`);
        // Refresh delivered invoices list
        await fetchDeliveredInvoices();
      } else {
        toast.warning(response.message || 'No invoices to sync');
      }
    } catch (err) {
      console.error('Error syncing delivered invoices:', err);
      toast.error(err.response?.data?.message || 'Error syncing delivered invoices');
    } finally {
      setSyncing(false);
    }
  };

  // Fetch pending invoices
  const fetchPendingInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPendingPeppolInvoices();
      if (response.success) {
        setPendingInvoices(response.data || []);
        setPendingPage(0);
      } else {
        setError(response.message || 'Failed to fetch pending invoices');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching pending invoices');
      console.error('Error fetching pending invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch delivered invoices
  const fetchDeliveredInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        PageNumber: deliveredPage + 1, // Backend uses 1-based pagination
        PageSize: deliveredRowsPerPage,
        SortBy: 'TimeReceived',
        SortDirection: 'desc'
      };
      
      const response = await getDeliveredPeppolInvoices(params);
      if (response.success) {
        const items = response.data?.Items || response.data || [];
        const totalItems = response.data?.TotalItems || response.totalItems || items.length;
        
        setDeliveredInvoices(Array.isArray(items) ? items : []);
        setTotalDeliveredCount(totalItems);
      } else {
        setError(response.message || 'Failed to fetch delivered invoices');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching delivered invoices');
      console.error('Error fetching delivered invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchDeliveredInvoices();
    } else {
      fetchPendingInvoices();
    }
  }, [activeTab]);
  
  // Reload delivered invoices when pagination changes
  useEffect(() => {
    if (activeTab === 0) {
      fetchDeliveredInvoices();
    }
    // eslint-disable-next-line
  }, [deliveredPage, deliveredRowsPerPage]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewDetails = (uniqueId) => {
    navigate(`/sales/received-peppol-invoices/${uniqueId}`, { state: { returnUrl: location.pathname + location.search } });
  };

  const handleMarkAsDelivered = (invoice) => {
    setConfirmDialog({ open: true, invoice });
  };

  const handleConfirmDelivery = async () => {
    const { invoice } = confirmDialog;
    setConfirmDialog({ open: false, invoice: null });
    
    try {
      const response = await markPeppolInvoiceAsDelivered(invoice.uniqueId);
      if (response.success) {
        await fetchPendingInvoices();
        setSuccessDialog({
          open: true,
          message: `Invoice ${invoice.documentNo} has been successfully marked as delivered!`
        });
      } else {
        toast.error(response.message || 'Failed to mark invoice as delivered');
      }
    } catch (err) {
      console.error('Error marking as delivered:', err);
      toast.error(err.response?.data?.message || 'Error marking invoice as delivered');
    }
  };

  const handleCancelDelivery = () => {
    setConfirmDialog({ open: false, invoice: null });
  };

  const handlePreviewAttachment = async (uniqueId) => {
    try {
      const response = await downloadPeppolAttachment(uniqueId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading attachment:', err);
      toast.error(err.response?.data?.message || 'Error downloading attachment');
    }
  };

  const handleOpenPaymentDialog = (invoice, isMarkingAsPaid) => {
    setPaymentDialog({ open: true, invoice, isMarkingAsPaid });
    setPaymentSubmitAttempted(false);
    if (isMarkingAsPaid) {
      setPaymentForm({ paidDate: new Date().toISOString().split('T')[0], paymentSource: '', paymentNotes: '' });
    }
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialog({ open: false, invoice: null, isMarkingAsPaid: true });
  };

  const handlePaymentSubmit = async () => {
    const { invoice, isMarkingAsPaid } = paymentDialog;

    if (isMarkingAsPaid) {
      setPaymentSubmitAttempted(true);
      if (!paymentForm.paidDate) {
        toast.error('Payment date is required');
        return;
      }
      if (!paymentForm.paymentSource) {
        toast.error('Payment method is required');
        return;
      }
    }

    const payload = {
      UniqueId: invoice.uniqueId,
      IsPaid: isMarkingAsPaid,
      PaidDate: isMarkingAsPaid ? paymentForm.paidDate || null : null,
      PaymentSource: isMarkingAsPaid ? paymentForm.paymentSource || null : null,
      PaymentNotes: isMarkingAsPaid ? paymentForm.paymentNotes || null : null,
    };
    try {
      const response = await updatePeppolInvoicePaymentStatus(payload);
      if (response.success) {
        toast.success(isMarkingAsPaid ? `Invoice marked as ${PAYMENT_STATUS.PAID.toLowerCase()} successfully` : `Invoice marked as ${PAYMENT_STATUS.NOT_PAID.toLowerCase()}`);
        handleClosePaymentDialog();
        await fetchDeliveredInvoices();
      } else {
        toast.error(response.message || response.errorMessage || 'Failed to update payment status');
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      toast.error(err.response?.data?.message || 'Error updating payment status');
    }
  };

  const getDocumentTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'creditnote':
        return <CreditCard sx={{ fontSize: 18 }} />;
      case 'invoice':
        return <Description sx={{ fontSize: 18 }} />;
      default:
        return <Description sx={{ fontSize: 18 }} />;
    }
  };

  const getDocumentTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'creditnote':
        return 'warning';
      case 'invoice':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const renderInvoiceTable = (invoices) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 3 }}>
          {error}
        </Alert>
      );
    }

    if (invoices.length === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CloudDownload sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No pending invoices</Typography>
        </Box>
      );
    }

    const pageStart = pendingPage * ROWS_PER_PAGE;
    const pageRows = invoices.slice(pageStart, pageStart + ROWS_PER_PAGE);

    return (
      <>
        <Alert severity="info" sx={{ m: 2 }}>
          To view invoice details, please mark the invoice as delivered first.
        </Alert>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Document No</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sender</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Receiver</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Received Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>PDF</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((invoice) => (
                <TableRow key={invoice.id} hover sx={{ '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.04)' } }}>
                  <TableCell>
                    <Chip
                      icon={getDocumentTypeIcon(invoice.documentType)}
                      label={invoice.documentType}
                      color={getDocumentTypeColor(invoice.documentType)}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{invoice.documentNo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{invoice.sender}</Typography>
                    <Typography variant="caption" color="text.secondary">{invoice.senderIdentifier}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{invoice.receiver}</Typography>
                    <Typography variant="caption" color="text.secondary">{invoice.receiverIdentifier}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(invoice.timeReceived)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.validationStatus}
                      color={invoice.validationStatus === 'approved' ? 'success' : 'default'}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {invoice.hasAttachment ? (
                      <Tooltip title="Preview PDF">
                        <IconButton
                          onClick={() => handlePreviewAttachment(invoice.uniqueId)}
                          sx={{
                            color: '#d32f2f',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            },
                          }}
                        >
                          <PictureAsPdf fontSize="medium" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="Mark as Delivered">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CheckCircle />}
                          onClick={() => handleMarkAsDelivered(invoice)}
                          color="success"
                        >
                          Mark as Delivered
                        </Button>
                      </Tooltip>
                      {invoice.hasAttachment && (
                        <Tooltip title="Preview PDF Attachment">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PictureAsPdf />}
                            onClick={() => handlePreviewAttachment(invoice.uniqueId)}
                            color="secondary"
                          >
                            Preview
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={invoices.length}
          page={pendingPage}
          onPageChange={(e, newPage) => setPendingPage(newPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
        />
      </>
    );
  };

  const renderDeliveredInvoicesTable = (invoices) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 3 }}>
          {error}
        </Alert>
      );
    }

    if (invoices.length === 0) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CloudDownload sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No delivered invoices</Typography>
        </Box>
      );
    }

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Document No</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sender</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Receiver</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Received Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Processed Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Payment</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>PDF</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id} hover sx={{ '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.04)' } }}>
                  <TableCell>
                    <Chip
                      icon={getDocumentTypeIcon(invoice.documentType)}
                      label={invoice.documentType}
                      color={getDocumentTypeColor(invoice.documentType)}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{invoice.documentNo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{invoice.sender}</Typography>
                    <Typography variant="caption" color="text.secondary">{invoice.senderIdentifier}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{invoice.receiver}</Typography>
                    <Typography variant="caption" color="text.secondary">{invoice.receiverIdentifier}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(invoice.timeReceived)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(invoice.processedDate)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.isProcessed ? 'Processed' : 'Pending'}
                      color={invoice.isProcessed ? 'success' : 'warning'}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.isPaid ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.NOT_PAID}
                      color={invoice.isPaid ? 'success' : 'default'}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {invoice.hasAttachment ? (
                      <Tooltip title="Preview PDF">
                        <IconButton
                          onClick={() => handlePreviewAttachment(invoice.uniqueId)}
                          sx={{
                            color: '#d32f2f',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            },
                          }}
                        >
                          <PictureAsPdf fontSize="medium" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="More actions">
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, invoice)}
                        size="small"
                        sx={{
                          color: '#667eea',
                          '&:hover': {
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          },
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalDeliveredCount}
          page={deliveredPage}
          onPageChange={handleDeliveredPageChange}
          rowsPerPage={deliveredRowsPerPage}
          onRowsPerPageChange={handleDeliveredPageSizeChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        />
      </>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Received Peppol Invoices
            </Typography>
            {activeTab === 1 ? (
              <Button
                variant="contained"
                startIcon={checking ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                onClick={handleCheckNewInvoices}
                disabled={checking}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #aaa 0%, #888 100%)',
                    color: '#fff',
                  },
                }}
              >
                {checking ? 'Checking...' : 'Check Pending Delivery Invoices'}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                onClick={handleSyncDeliveredInvoices}
                disabled={syncing}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #aaa 0%, #888 100%)',
                    color: '#fff',
                  },
                }}
              >
                {syncing ? 'Syncing...' : 'Sync Received Invoices'}
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Manage your incoming Peppol invoices and credit notes
          </Typography>
        </Box>

        {/* Tabs and Content */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: '#fff',
              '& .MuiTab-root': {
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 64,
                px: 4,
              },
              '& .Mui-selected': {
                color: '#667eea',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea',
                height: 3,
              },
            }}
          >
            <Tab
              label={`Received/Delivered (${totalDeliveredCount})`}
              icon={<CheckCircle />}
              iconPosition="start"
            />
            <Tab
              label={`Pending Delivery (${pendingInvoices.length})`}
              icon={<CloudDownload />}
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ backgroundColor: '#fff', minHeight: 400 }}>
            {activeTab === 0 && renderDeliveredInvoicesTable(deliveredInvoices)}
            {activeTab === 1 && renderInvoiceTable(pendingInvoices)}
          </Box>
        </Paper>
      </Container>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelDelivery}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2.5,
          }}
        >
          <Warning sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Confirm Delivery
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 3, pb: 2 }}>
          <DialogContentText sx={{ mb: 2, color: 'text.primary', fontSize: '1rem' }}>
            Are you sure you want to mark this invoice as delivered?
          </DialogContentText>
          {confirmDialog.invoice && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid rgba(102, 126, 234, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Document No:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {confirmDialog.invoice.documentNo}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Type:
                </Typography>
                <Chip
                  label={confirmDialog.invoice.documentType}
                  size="small"
                  color={confirmDialog.invoice.documentType === 'Invoice' ? 'primary' : 'warning'}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Sender:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>
                  {confirmDialog.invoice.sender}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Receiver:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>
                  {confirmDialog.invoice.receiver}
                </Typography>
              </Box>
            </Paper>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            This action will move the invoice to the "Received/Delivered" tab.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleCancelDelivery}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelivery}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            sx={{
              textTransform: 'none',
              px: 3,
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
            }}
          >
            Confirm Delivery
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialog.open}
        onClose={() => setSuccessDialog({ open: false, message: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2.5,
          }}
        >
          <CheckCircle sx={{ fontSize: 32 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Success!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 3, pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              py: 2,
            }}
          >
            <CheckCircle
              sx={{
                fontSize: 80,
                color: '#4caf50',
                mb: 2,
              }}
            />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Invoice Delivered Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {successDialog.message}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button
            onClick={() => setSuccessDialog({ open: false, message: '' })}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
              color: '#fff',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 120,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #45a049 0%, #5caf5c 100%)',
                boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialog.open}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: paymentDialog.isMarkingAsPaid
              ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
              : 'linear-gradient(135deg, #f44336 0%, #e57373 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2.5,
          }}
        >
          {paymentDialog.isMarkingAsPaid ? (
            <AttachMoney sx={{ fontSize: 32 }} />
          ) : (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <EuroSymbol sx={{ fontSize: 32 }} />
              <Block sx={{ fontSize: 32, position: 'absolute', color: '#fff' }} />
            </Box>
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {paymentDialog.isMarkingAsPaid ? `Mark Invoice as ${PAYMENT_STATUS.PAID}` : `Mark Invoice as ${PAYMENT_STATUS.NOT_PAID}`}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* Invoice summary */}
          {paymentDialog.invoice && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2.5,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid rgba(102, 126, 234, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Document No:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{paymentDialog.invoice.documentNo}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Sender:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{paymentDialog.invoice.sender}</Typography>
              </Box>
              {paymentDialog.invoice.isPaid && paymentDialog.invoice.paidDate && !paymentDialog.isMarkingAsPaid && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Paid On:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(paymentDialog.invoice.paidDate)}</Typography>
                </Box>
              )}
            </Paper>
          )}
          {paymentDialog.isMarkingAsPaid ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              <TextField
                label="Payment Date"
                type="date"
                value={paymentForm.paidDate}
                onChange={(e) => setPaymentForm({ ...paymentForm, paidDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                error={paymentSubmitAttempted && !paymentForm.paidDate}
                helperText={paymentSubmitAttempted && !paymentForm.paidDate ? 'Payment date is required' : ''}
              />
              <FormControl fullWidth required error={paymentSubmitAttempted && !paymentForm.paymentSource}>
                <InputLabel>Payment Method *</InputLabel>
                <Select
                  value={paymentForm.paymentSource}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentSource: e.target.value })}
                  label="Payment Method *"
                >
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Bancontact">Bancontact</MenuItem>
                  <MenuItem value="Virement">Virement</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Payment Notes"
                value={paymentForm.paymentNotes}
                onChange={(e) => setPaymentForm({ ...paymentForm, paymentNotes: e.target.value })}
                multiline
                rows={3}
                fullWidth
                inputProps={{ maxLength: 1000 }}
                placeholder="Optional notes about this payment..."
              />
            </Box>
          ) : (
            <DialogContentText sx={{ mt: 1, color: 'text.primary', fontSize: '1rem' }}>
              Are you sure you want to mark this invoice as {PAYMENT_STATUS.NOT_PAID.toLowerCase()}? This will clear all payment details.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleClosePaymentDialog}
            variant="outlined"
            sx={{ textTransform: 'none', px: 3, borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaymentSubmit}
            variant="contained"
            color={paymentDialog.isMarkingAsPaid ? 'success' : 'error'}
            startIcon={
              paymentDialog.isMarkingAsPaid ? (
                <AttachMoney />
              ) : (
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <EuroSymbol />
                  <Block sx={{ position: 'absolute', fontSize: '1.2em' }} />
                </Box>
              )
            }
            sx={{ textTransform: 'none', px: 3, borderRadius: 2, fontWeight: 600 }}
          >
            {paymentDialog.isMarkingAsPaid ? 'Confirm Payment' : `Mark as ${PAYMENT_STATUS.NOT_PAID}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleViewDetails(selectedInvoice?.uniqueId);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleOpenPaymentDialog(selectedInvoice, !selectedInvoice?.isPaid);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {selectedInvoice?.isPaid ? (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <EuroSymbol fontSize="small" />
                <Block fontSize="small" sx={{ position: 'absolute', color: '#d32f2f' }} />
              </Box>
            ) : (
              <EuroSymbol fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedInvoice?.isPaid ? `Mark as ${PAYMENT_STATUS.NOT_PAID}` : `Mark as ${PAYMENT_STATUS.PAID}`}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ReceivedPeppolInvoices;
